import { api, formatTenne } from "@awtoban/shared";
import { Label, Switch } from "@awtoban/ui";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { Locale } from "../../i18n";
import { bcp47, HqHeader } from "../../lib/HqHeader";
import { type GroupKind, type GroupRow, TruckBuilder } from "./TruckBuilder";

interface City {
  id: number;
  name_tk: string;
  name_ru: string;
  name_en: string;
}

interface CompareLine {
  kind: string;
  amount_tenne: number;
  input_value_tn: string | null;
  rate_tenne_per_km: string;
  distance_km: string;
}

interface CompareQuote {
  line_items: CompareLine[];
  subtotal_tenne: number;
  permit_fee_tenne: number;
  total_tenne: number;
  engine_version: string;
}

interface CompareRow {
  id: string;
  label: string;
  version: string;
  quote: CompareQuote | null;
  total_tenne: number | null;
  error: string | null;
  total_delta_vs_authoritative: number | null;
}

interface CompareResponse {
  distance_km: string;
  results: CompareRow[];
}

function cityName(c: City, locale: Locale): string {
  if (locale === "ru") return c.name_ru || c.name_tk;
  if (locale === "en") return c.name_en || c.name_tk;
  return c.name_tk;
}

export function ComparePage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const tag = bcp47(locale);

  const citiesQuery = useQuery({
    queryKey: ["cities"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/cities/", {});
      if (error || !data) throw new Error("cities_failed");
      return data as unknown as City[];
    },
  });
  const cities = citiesQuery.data ?? [];

  const [fromCityId, setFromCityId] = React.useState("");
  const [toCityId, setToCityId] = React.useState("");
  const [frontAxleTn, setFrontAxleTn] = React.useState("6");
  const [isOversized, setIsOversized] = React.useState(true);
  const [requiresPermit, setRequiresPermit] = React.useState(true);
  const [groups, setGroups] = React.useState<GroupRow[]>([
    { kind: "twin", weight: "28.5" },
    { kind: "twin", weight: "28.5" },
  ]);

  const addGroup = (kind: GroupKind) => setGroups((gs) => [...gs, { kind, weight: "" }]);
  const removeGroup = (i: number) => setGroups((gs) => gs.filter((_, j) => j !== i));
  const setWeight = (i: number, weight: string) =>
    setGroups((gs) => gs.map((g, j) => (j === i ? { ...g, weight } : g)));

  // The runnable payload, or null when the inputs aren't complete yet.
  const payload = React.useMemo(() => {
    const vg = groups.filter((g) => g.weight.trim() !== "");
    const ready = fromCityId !== "" && toCityId !== "" && fromCityId !== toCityId && vg.length > 0;
    if (!ready) return null;
    return {
      from_city_id: Number(fromCityId),
      to_city_id: Number(toCityId),
      front_axle_tn: frontAxleTn || "0",
      is_oversized: isOversized,
      requires_permit: requiresPermit,
      axle_groups: vg.map((g) => ({ kind: g.kind, weight_tn: g.weight })),
    };
  }, [fromCityId, toCityId, frontAxleTn, isOversized, requiresPermit, groups]);

  // Auto-compare: debounce input changes, then refetch automatically.
  const [applied, setApplied] = React.useState<typeof payload>(null);
  React.useEffect(() => {
    if (!payload) {
      setApplied(null);
      return;
    }
    const id = setTimeout(() => setApplied(payload), 350);
    return () => clearTimeout(id);
  }, [payload]);

  const query = useQuery({
    queryKey: ["pricing-compare", applied],
    enabled: applied !== null,
    placeholderData: (prev) => prev, // keep last results visible while recomputing
    queryFn: async (): Promise<CompareResponse> => {
      const { data, error } = await api.POST("/api/v1/pricing/compare", {
        body: applied as never,
      });
      if (error || !data) throw new Error("compare_failed");
      return data as unknown as CompareResponse;
    },
  });

  const ready = payload !== null;
  const results = query.data?.results ?? [];

  return (
    <div className="min-h-screen bg-paper-soft">
      <HqHeader active="pricing" />
      <main className="mx-auto max-w-[1100px] space-y-4 px-4 py-4 sm:px-6 sm:py-6">
        <p className="text-[13px] text-ink-muted">{t("compare.intro")}</p>

        <section className="rounded-md border border-line-strong bg-paper p-4">
          <div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <Label htmlFor="from">{t("compare.from")}</Label>
                <select
                  id="from"
                  className="h-10 w-full rounded-md border border-line-strong bg-paper px-2 text-sm"
                  value={fromCityId}
                  onChange={(e) => setFromCityId(e.target.value)}
                >
                  <option value="">{t("compare.select_placeholder")}</option>
                  {cities.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {cityName(c, locale)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="to">{t("compare.to")}</Label>
                <select
                  id="to"
                  className="h-10 w-full rounded-md border border-line-strong bg-paper px-2 text-sm"
                  value={toCityId}
                  onChange={(e) => setToCityId(e.target.value)}
                >
                  <option value="">{t("compare.select_placeholder")}</option>
                  {cities.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {cityName(c, locale)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end gap-4 sm:col-span-2">
                <div className="flex items-center gap-2">
                  <Switch id="oversize" checked={isOversized} onCheckedChange={setIsOversized} />
                  <Label htmlFor="oversize">{t("compare.oversized")}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="permit"
                    checked={requiresPermit}
                    onCheckedChange={setRequiresPermit}
                  />
                  <Label htmlFor="permit">{t("compare.permit")}</Label>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <TruckBuilder
                front={frontAxleTn}
                onFront={setFrontAxleTn}
                groups={groups}
                onAdd={addGroup}
                onRemove={removeGroup}
                onWeight={setWeight}
              />
            </div>
          </div>
          {query.isError ? <p className="mt-3 text-danger text-sm">{t("compare.error")}</p> : null}
        </section>

        <div className="flex items-center justify-between">
          <h2 className="font-medium text-sm">{t("compare.results")}</h2>
          {query.isFetching ? (
            <span className="text-[12px] text-ink-muted">{t("compare.running")}</span>
          ) : null}
        </div>

        {results.length > 0 ? (
          <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {results.map((r) => (
              <EngineCard key={r.id} row={r} tag={tag} />
            ))}
          </section>
        ) : (
          <p className="rounded-md border border-line border-dashed bg-paper px-4 py-6 text-center text-[13px] text-ink-muted">
            {ready ? t("compare.running") : t("compare.pick_hint")}
          </p>
        )}
      </main>
    </div>
  );
}

function DeltaBadge({ row, tag }: { row: CompareRow; tag: string }) {
  const { t } = useTranslation();
  if (row.id === "v1") {
    return (
      <span className="rounded bg-ink px-1.5 py-0.5 text-[10px] text-paper uppercase tracking-wider">
        {t("compare.authoritative")}
      </span>
    );
  }
  const d = row.total_delta_vs_authoritative;
  if (d == null) return null;
  const tone = d === 0 ? "text-ink-muted" : d > 0 ? "text-danger" : "text-success";
  return (
    <span className={`text-xs tabular-nums ${tone}`}>
      {d > 0 ? "+" : ""}
      {formatTenne(d, tag)}
    </span>
  );
}

function EngineCard({ row, tag }: { row: CompareRow; tag: string }) {
  const { t } = useTranslation();
  const unitT = t("compare.unit_t");
  const unitKm = t("compare.unit_km");
  // v3 prices the tonnes a group is OVER its legal limit at a single global
  // tariff (manat per tonne·km, set by the truck's total overload tier). The
  // line carries `over × tariff` as its per-km rate, which reads as a confusing
  // bare "/km". Recover the underlying tariff from any line that has an overage
  // so we can show the honest `over t × tariff × distance` instead.
  const overloadTariffTenne =
    row.id === "v3"
      ? (() => {
          const ref = row.quote?.line_items.find(
            (l) => l.kind !== "mass" && l.kind !== "permit" && Number(l.input_value_tn) > 0,
          );
          return ref ? Number(ref.rate_tenne_per_km) / Number(ref.input_value_tn) : null;
        })()
      : null;
  const fmtTariff = (tenne: number) =>
    new Intl.NumberFormat(tag, { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(
      tenne / 100,
    );

  // The muted formula under each line. "" → render nothing (e.g. the flat permit fee).
  const derivationFor = (li: CompareLine): string => {
    if (li.kind === "permit") return "";
    const dist = `${Number(li.distance_km)} ${unitKm}`;
    if (row.id === "v3" && li.kind !== "mass" && overloadTariffTenne != null) {
      // `over t × tariff TMT/t·km × distance km` — the actual НАш formula.
      return `${Number(li.input_value_tn)} ${unitT} × ${fmtTariff(overloadTariffTenne)} TMT/${unitT}·${unitKm} × ${dist}`;
    }
    // Rate-table line: the load looks up a per-km rate (→), then × distance.
    const lead = li.input_value_tn ? `${Number(li.input_value_tn)} ${unitT} → ` : "";
    return `${lead}${formatTenne(Number(li.rate_tenne_per_km), tag)} TMT/${unitKm} × ${dist}`;
  };

  return (
    <article
      className={`rounded-md border bg-paper p-4 ${row.id === "v1" ? "border-ink" : "border-line-strong"}`}
    >
      <header className="mb-3 flex items-baseline justify-between gap-2">
        <div>
          <h2 className="font-medium text-sm">{row.label}</h2>
          <p className="text-[11px] text-ink-muted">{row.version}</p>
        </div>
        <DeltaBadge row={row} tag={tag} />
      </header>

      {row.error ? <p className="text-danger text-sm">{row.error}</p> : null}
      {row.quote ? (
        <>
          <div className="space-y-1.5 text-[13px]">
            {row.quote.line_items.map((li, i) => {
              const derivation = derivationFor(li);
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: a kind can repeat (e.g. two twin groups)
                <div key={`${li.kind}-${i}`} className={li.amount_tenne === 0 ? "opacity-50" : ""}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span>{t(`compare.kinds.${li.kind}`)}</span>
                    <span className="tabular-nums">{formatTenne(li.amount_tenne, tag)}</span>
                  </div>
                  {derivation ? (
                    <div className="text-[11px] text-ink-muted tabular-nums">{derivation}</div>
                  ) : null}
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex items-baseline justify-between border-line border-t pt-2">
            <span className="font-medium text-sm">{t("compare.total")}</span>
            <span className="font-semibold text-base tabular-nums">
              {formatTenne(row.quote.total_tenne, tag)} TMT
            </span>
          </div>
        </>
      ) : null}
    </article>
  );
}
